import { createContext, ReactNode, useContext, useState } from 'react';
import { t } from 'i18next';
import { useRouter } from 'next/router';
// configs
import {
    UTM_CAMPAIGN_CONTENT_CREDENTIAL,
    UTM_CAMPAIGN_FIRST_CREDENTIAL,
    UTM_CAMPAIGN_MEDIUM_CREDENTIAL,
    UTM_CAMPAIGN_SOURCE_CREDENTIAL,
    UTM_CAMPAIGN_TERM_CREDENTIAL,
} from 'config/flags';
// contexts
import { usePointOfSale } from 'context/point-of-sale-context';
// utils && hooks
import { useUser } from 'utils/hooks';

export type Step = {
    icon: (color: string) => ReactNode;
    confirmIcon?: (color: string) => ReactNode;
    errorIcon?: (color: string) => ReactNode;
};

export type HeaderStep = {
    headerTitle: (value) => string;
    headerSubTitle?: (value) => string;
    ErrorHeaderTitle?: (value) => string;
    ErrorHeaderSubTitle?: (value) => string;
};

type ContextProps = {
    steps: Step[];
    headerSteps: HeaderStep[];
    currentHeaderStep: number;
    currentStep: number;
    timeLeft: number;
    quantityRequestedProducts: number;
    handleTimeLeft: (time: any) => void;
    handleQuantityRequestedProducts: (quantity: number) => void;
    handleCurrentStep: (stepNumber: number) => void;
    handleCurrentHeaderStep: (stepNumber: number) => void;
    handleProgress: (progress: number) => void;
    progress: number;
    orderId: number | null;
    remainingAttempts: number | null;
    drugManufacturerStatusList: any[];
    handleOrderId: (value: number) => void;
    handleRemainingAttempts: (value: number) => void;
    handleDrugManufacturerStatusList: (value: any) => void;
    redirectToComparative: () => void;
    failed: boolean;
    handleFailed: () => void;
    completed: boolean;
    handleCompleted: () => void;
    hasErrors: boolean;
    handleErrors: () => void;
    resetStates: () => void;
};

const defaultValues: ContextProps = {
    currentStep: 1,
    currentHeaderStep: 1,
    steps: [
        {
            icon: (color: string) => (
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path
                        d="M5.02497 21C4.47497 21 4.00413 20.8042 3.61247 20.4125C3.2208 20.0208 3.02497 19.55 3.02497 19V11.05C2.64163 10.7 2.3458 10.25 2.13747 9.7C1.92913 9.15 1.92497 8.55 2.12497 7.9L3.17497 4.5C3.3083 4.06667 3.5458 3.70833 3.88747 3.425C4.22913 3.14167 4.62497 3 5.07497 3H18.975C19.425 3 19.8166 3.1375 20.15 3.4125C20.4833 3.6875 20.725 4.05 20.875 4.5L21.925 7.9C22.125 8.55 22.1208 9.14167 21.9125 9.675C21.7041 10.2083 21.4083 10.6667 21.025 11.05V19C21.025 19.55 20.8291 20.0208 20.4375 20.4125C20.0458 20.8042 19.575 21 19.025 21H5.02497ZM14.225 10C14.675 10 15.0166 9.84583 15.25 9.5375C15.4833 9.22917 15.575 8.88333 15.525 8.5L14.975 5H13.025V8.7C13.025 9.05 13.1416 9.35417 13.375 9.6125C13.6083 9.87083 13.8916 10 14.225 10ZM9.72497 10C10.1083 10 10.4208 9.87083 10.6625 9.6125C10.9041 9.35417 11.025 9.05 11.025 8.7V5H9.07497L8.52497 8.5C8.4583 8.9 8.5458 9.25 8.78747 9.55C9.02913 9.85 9.34163 10 9.72497 10ZM5.27497 10C5.57497 10 5.83747 9.89167 6.06247 9.675C6.28747 9.45833 6.42497 9.18333 6.47497 8.85L7.02497 5H5.07497L4.07497 8.35C3.97497 8.68333 4.02913 9.04167 4.23747 9.425C4.4458 9.80833 4.79163 10 5.27497 10ZM18.775 10C19.2583 10 19.6083 9.80833 19.825 9.425C20.0416 9.04167 20.0916 8.68333 19.975 8.35L18.925 5H17.025L17.575 8.85C17.625 9.18333 17.7625 9.45833 17.9875 9.675C18.2125 9.89167 18.475 10 18.775 10ZM5.02497 19H19.025V11.95C18.9416 11.9833 18.8875 12 18.8625 12H18.775C18.325 12 17.9291 11.925 17.5875 11.775C17.2458 11.625 16.9083 11.3833 16.575 11.05C16.275 11.35 15.9333 11.5833 15.55 11.75C15.1666 11.9167 14.7583 12 14.325 12C13.875 12 13.4541 11.9167 13.0625 11.75C12.6708 11.5833 12.325 11.35 12.025 11.05C11.7416 11.35 11.4125 11.5833 11.0375 11.75C10.6625 11.9167 10.2583 12 9.82497 12C9.34163 12 8.90413 11.9167 8.51247 11.75C8.1208 11.5833 7.77497 11.35 7.47497 11.05C7.12497 11.4 6.77913 11.6458 6.43747 11.7875C6.0958 11.9292 5.7083 12 5.27497 12H5.16247C5.1208 12 5.07497 11.9833 5.02497 11.95V19Z"
                        fill={color}
                    />
                </svg>
            ),
        },
        {
            icon: (color: string) => (
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path
                        d="M14.25 21.4C13.8667 21.7833 13.3917 21.975 12.825 21.975C12.2583 21.975 11.7833 21.7833 11.4 21.4L2.6 12.6C2.41667 12.4167 2.27083 12.2 2.1625 11.95C2.05417 11.7 2 11.4333 2 11.15V4C2 3.45 2.19583 2.97917 2.5875 2.5875C2.97917 2.19583 3.45 2 4 2H11.15C11.4333 2 11.7 2.05417 11.95 2.1625C12.2 2.27083 12.4167 2.41667 12.6 2.6L21.4 11.425C21.7833 11.8083 21.975 12.2792 21.975 12.8375C21.975 13.3958 21.7833 13.8667 21.4 14.25L14.25 21.4ZM12.825 20L19.975 12.85L11.15 4H4V11.15L12.825 20ZM6.5 8C6.91667 8 7.27083 7.85417 7.5625 7.5625C7.85417 7.27083 8 6.91667 8 6.5C8 6.08333 7.85417 5.72917 7.5625 5.4375C7.27083 5.14583 6.91667 5 6.5 5C6.08333 5 5.72917 5.14583 5.4375 5.4375C5.14583 5.72917 5 6.08333 5 6.5C5 6.91667 5.14583 7.27083 5.4375 7.5625C5.72917 7.85417 6.08333 8 6.5 8Z"
                        fill={color}
                    />
                </svg>
            ),
        },
        {
            icon: (color: string) => (
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path
                        d="M16 11C16.2833 11 16.5208 10.9042 16.7125 10.7125C16.9042 10.5208 17 10.2833 17 10C17 9.71667 16.9042 9.47917 16.7125 9.2875C16.5208 9.09583 16.2833 9 16 9C15.7167 9 15.4792 9.09583 15.2875 9.2875C15.0958 9.47917 15 9.71667 15 10C15 10.2833 15.0958 10.5208 15.2875 10.7125C15.4792 10.9042 15.7167 11 16 11ZM9 9H12C12.2833 9 12.5208 8.90417 12.7125 8.7125C12.9042 8.52083 13 8.28333 13 8C13 7.71667 12.9042 7.47917 12.7125 7.2875C12.5208 7.09583 12.2833 7 12 7H9C8.71667 7 8.47917 7.09583 8.2875 7.2875C8.09583 7.47917 8 7.71667 8 8C8 8.28333 8.09583 8.52083 8.2875 8.7125C8.47917 8.90417 8.71667 9 9 9ZM5.925 21C5.54167 21 5.17083 20.8667 4.8125 20.6C4.45417 20.3333 4.21667 20.0083 4.1 19.625C3.68333 18.225 3.34167 17.0125 3.075 15.9875C2.80833 14.9625 2.59583 14.0625 2.4375 13.2875C2.27917 12.5125 2.16667 11.825 2.1 11.225C2.03333 10.625 2 10.05 2 9.5C2 7.96667 2.53333 6.66667 3.6 5.6C4.66667 4.53333 5.96667 4 7.5 4H12.5C12.95 3.4 13.5208 2.91667 14.2125 2.55C14.9042 2.18333 15.6667 2 16.5 2C16.9167 2 17.2708 2.14583 17.5625 2.4375C17.8542 2.72917 18 3.08333 18 3.5C18 3.6 17.9875 3.7 17.9625 3.8C17.9375 3.9 17.9083 3.99167 17.875 4.075C17.8083 4.25833 17.7458 4.44167 17.6875 4.625C17.6292 4.80833 17.5833 5.00833 17.55 5.225L19.825 7.5H21C21.2833 7.5 21.5208 7.59583 21.7125 7.7875C21.9042 7.97917 22 8.21667 22 8.5V13.75C22 13.9667 21.9375 14.1583 21.8125 14.325C21.6875 14.4917 21.5167 14.6167 21.3 14.7L19.175 15.4L17.925 19.575C17.7917 20.0083 17.55 20.3542 17.2 20.6125C16.85 20.8708 16.45 21 16 21H14C13.45 21 12.9792 20.8042 12.5875 20.4125C12.1958 20.0208 12 19.55 12 19H10C10 19.55 9.80417 20.0208 9.4125 20.4125C9.02083 20.8042 8.55 21 8 21H5.925ZM6 19H8V17H14V19H16L17.55 13.85L20 13.025V9.5H19L15.5 6C15.5 5.66667 15.5208 5.34167 15.5625 5.025C15.6042 4.70833 15.6667 4.4 15.75 4.1C15.2667 4.23333 14.8417 4.4625 14.475 4.7875C14.1083 5.1125 13.8417 5.51667 13.675 6H7.5C6.53333 6 5.70833 6.34167 5.025 7.025C4.34167 7.70833 4 8.53333 4 9.5C4 10.1833 4.175 11.3542 4.525 13.0125C4.875 14.6708 5.36667 16.6667 6 19Z"
                        fill={color}
                    />
                </svg>
            ),
            confirmIcon: (color: string) => (
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path
                        d="M9.54999 15.15L18.025 6.675C18.225 6.475 18.4625 6.375 18.7375 6.375C19.0125 6.375 19.25 6.475 19.45 6.675C19.65 6.875 19.75 7.1125 19.75 7.3875C19.75 7.6625 19.65 7.9 19.45 8.1L10.25 17.3C10.05 17.5 9.81666 17.6 9.54999 17.6C9.28333 17.6 9.04999 17.5 8.84999 17.3L4.54999 13C4.34999 12.8 4.25416 12.5625 4.26249 12.2875C4.27083 12.0125 4.37499 11.775 4.57499 11.575C4.77499 11.375 5.01249 11.275 5.28749 11.275C5.56249 11.275 5.79999 11.375 5.99999 11.575L9.54999 15.15Z"
                        fill={color}
                    />
                </svg>
            ),
            errorIcon: (color: string) => (
                <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40" fill="none">
                    <path
                        d="M20 22.3334L11.8333 30.5001C11.5277 30.8056 11.1388 30.9584 10.6666 30.9584C10.1944 30.9584 9.80551 30.8056 9.49996 30.5001C9.1944 30.1945 9.04163 29.8056 9.04163 29.3334C9.04163 28.8612 9.1944 28.4723 9.49996 28.1667L17.6666 20.0001L9.49996 11.8334C9.1944 11.5279 9.04163 11.139 9.04163 10.6667C9.04163 10.1945 9.1944 9.80564 9.49996 9.50008C9.80551 9.19453 10.1944 9.04175 10.6666 9.04175C11.1388 9.04175 11.5277 9.19453 11.8333 9.50008L20 17.6667L28.1666 9.50008C28.4722 9.19453 28.8611 9.04175 29.3333 9.04175C29.8055 9.04175 30.1944 9.19453 30.5 9.50008C30.8055 9.80564 30.9583 10.1945 30.9583 10.6667C30.9583 11.139 30.8055 11.5279 30.5 11.8334L22.3333 20.0001L30.5 28.1667C30.8055 28.4723 30.9583 28.8612 30.9583 29.3334C30.9583 29.8056 30.8055 30.1945 30.5 30.5001C30.1944 30.8056 29.8055 30.9584 29.3333 30.9584C28.8611 30.9584 28.4722 30.8056 28.1666 30.5001L20 22.3334Z"
                        fill={color}
                    />
                </svg>
            ),
        },
    ],
    headerSteps: [
        {
            headerTitle: (v) => t('progressBarPrice.title1', { value: v }),
            headerSubTitle: () => t('progressBarPrice.title2'),
        },
        {
            headerTitle: (v) => t('progressBarPrice.body1'),
            headerSubTitle: (v) => t('progressBarPrice.body2'),
        },
        {
            headerTitle: (v) => t('progressBarPrice.body3'),
            ErrorHeaderTitle: (v) => t('progressBarPrice.body5'),
            ErrorHeaderSubTitle: (v) => t('progressBarPrice.body6'),
        },
    ],
    timeLeft: 0,
    quantityRequestedProducts: 0,
    handleTimeLeft: (time: any) => {},
    handleQuantityRequestedProducts: (quantity: number) => {},
    handleCurrentStep: (stepNumber: number) => {},
    handleCurrentHeaderStep: (stepNumber: number) => {},
    handleProgress: (progress: number) => {},
    progress: 3,
    orderId: null,
    remainingAttempts: null,
    drugManufacturerStatusList: [],
    handleOrderId: (value: number) => {},
    handleRemainingAttempts: (value: number) => {},
    handleDrugManufacturerStatusList: (value: any) => {},
    redirectToComparative: () => {},
    failed: false,
    handleFailed: () => {},
    completed: false,
    handleCompleted: () => {},
    hasErrors: false,
    handleErrors: () => {},
    resetStates: () => {},
};

type Props = {
    children: React.ReactNode;
};

const ProgressBarPriceContext = createContext<ContextProps>(defaultValues);

export const ProgressBarPriceProvider = ({ children }: Props) => {
    const { push, query } = useRouter();
    const { pointOfSale } = usePointOfSale();
    const [orderId, setOrderId] = useState<number | null>(defaultValues.orderId);
    const [remainingAttempts, setRemainingAttempts] = useState<number | null>(defaultValues.remainingAttempts);
    const [drugManufacturerStatusList, setDrugManufacturerStatusList] = useState<any[]>(
        defaultValues.drugManufacturerStatusList,
    );
    const { user } = useUser({});

    // Son 3 steps: conexión a droguerias - P.G.C - Todo ok o todo mal.
    const [currentStep, setCurrentStep] = useState<number>(defaultValues.currentStep);
    // Son 3 header steps (titulos): conexión a droguerias - P.G.C - Todo ok o todo mal.
    const [currentHeaderStep, setCurrentHeaderStep] = useState<number>(defaultValues.currentHeaderStep);
    const [progress, setProgress] = useState<number>(defaultValues.progress);
    const [timeLeft, setTimeLeft] = useState<number>(defaultValues.timeLeft);
    const [quantityRequestedProducts, setQuantityRequestedProducts] = useState<number>(
        defaultValues.quantityRequestedProducts,
    );
    const [hasErrors, setHasErrors] = useState<boolean>(defaultValues.hasErrors);

    const steps = defaultValues.steps;
    const headerSteps = defaultValues.headerSteps;
    const [failed, setFailed] = useState<boolean>(defaultValues.failed);

    // completed progress bar
    const [completed, setCompleted] = useState<boolean>(false);

    const handleTimeLeft = (time: any) => {
        setTimeLeft(time);
    };

    const handleQuantityRequestedProducts = (quantity: number) => {
        setQuantityRequestedProducts(quantity);
    };

    const handleCurrentStep = (stepNumber: number) => {
        setCurrentStep(stepNumber);
    };

    const handleCurrentHeaderStep = (stepNumber: number) => {
        setCurrentHeaderStep(stepNumber);
    };

    const handleProgress = (progress: number) => {
        setProgress(progress);
    };

    const handleOrderId = (orderId: number) => {
        setOrderId(orderId);
    };

    const handleRemainingAttempts = (remainingAttempts: number) => {
        setRemainingAttempts(remainingAttempts);
    };

    const handleDrugManufacturerStatusList = (drugManufacturerStatusList: any) => {
        setDrugManufacturerStatusList(drugManufacturerStatusList);
    };

    const handleFailed = () => {
        setFailed(true);
    };

    const handleCompleted = () => {
        setCompleted(true);
    };

    const handleErrors = () => {
        setHasErrors(true);
    };

    const resetStates = (): void => {
        setCurrentStep(defaultValues.currentStep);
        setCurrentHeaderStep(defaultValues.currentHeaderStep);
        setTimeLeft(defaultValues.timeLeft);
        setQuantityRequestedProducts(defaultValues.quantityRequestedProducts);
        setProgress(defaultValues.progress);
        setOrderId(defaultValues.orderId);
        setRemainingAttempts(defaultValues.remainingAttempts);
        setDrugManufacturerStatusList(defaultValues.drugManufacturerStatusList);
        setFailed(defaultValues.failed);
        setCompleted(defaultValues.completed);
        setHasErrors(defaultValues.hasErrors);
    };

    const redirectToComparative = (): void => {
        // Guardar drugManufacturerStatusList en localStorage
        localStorage.setItem('drugManufacturerStatusList', JSON.stringify(drugManufacturerStatusList));
        push({
            pathname: `/${query?.clientId}/efficient-purchase/${pointOfSale?.id}/product-comparison`,
            query: {
                orderId,
                ...(!!user?.free_trial_first_get_price && {
                    utm_campaign: UTM_CAMPAIGN_FIRST_CREDENTIAL,
                    utm_source: UTM_CAMPAIGN_SOURCE_CREDENTIAL,
                    utm_medium: UTM_CAMPAIGN_MEDIUM_CREDENTIAL,
                    utm_term: UTM_CAMPAIGN_TERM_CREDENTIAL,
                    utm_content: UTM_CAMPAIGN_CONTENT_CREDENTIAL,
                }),
            },
        });
    };

    const value: ContextProps = {
        currentStep,
        currentHeaderStep,
        steps,
        headerSteps,
        timeLeft,
        quantityRequestedProducts,
        handleTimeLeft,
        handleQuantityRequestedProducts,
        handleCurrentStep,
        handleCurrentHeaderStep,
        progress,
        handleProgress,
        orderId,
        remainingAttempts,
        drugManufacturerStatusList,
        handleOrderId,
        handleRemainingAttempts,
        handleDrugManufacturerStatusList,
        redirectToComparative,
        failed,
        handleFailed,
        completed,
        handleCompleted,
        hasErrors,
        handleErrors,
        resetStates,
    };

    return <ProgressBarPriceContext.Provider value={value}>{children}</ProgressBarPriceContext.Provider>;
};

export const useProgressBarPriceContext = () => useContext(ProgressBarPriceContext);
